import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { useScrollToBottom } from '../../../hooks/useScrollToBottom';
import { Form } from '../../../components/ui/Form';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  StyledModalBody,
  StyledModalV2
} from '../../../components/Modals';
import {
  editBuilding,
  clearBuilding,
  setGlobalSelectedBuilding,
  getBuildings,
  getAllBuildings
} from '../../../redux/actions/building';
import { pushResidentsToIntercom } from '../../../redux/actions/intercom';
import { changeOpenedModal } from '../../../redux/actions/headerModal';
import { setToast } from '../../../redux/actions/window';
import { INTERCOM_NAME_SETTINGS, INTERCOM_UNIT_NAME_DISPLAY_SETTINGS } from '../../../helper/constants';
import { ButtonBodyVariant } from '../../../components/ui/ButtonVariant';
import IntercomSettingsFormInputs from '../IntercomSettingsFormInputs';

const EditIntercomSettingsModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const submitInProgressRef = useRef(false);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const [isUpdateInProgress, setIsUpdateInProgress] = useState(false);

  const { globalSelectedBuilding, pagination } = useSelector((state) => state.building);

  // Dropdown stores full building object (from GET /orgs/:id/buildings list) with value/label; "All buildings" has value 0
  const buildingId = globalSelectedBuilding?.id ?? globalSelectedBuilding?.value;
  const numericId = buildingId != null && buildingId !== '' ? Number(buildingId) : NaN;
  const isValidBuildingId = Number.isInteger(numericId) && numericId > 0;

  // Use the already-loaded building from the dropdown (same source as Intercom page) — avoids GET /buildings/:id which 404s on this backend
  const building = isValidBuildingId ? globalSelectedBuilding : null;

  const schema = Yup.object().shape({
    intercom_name_format: Yup.string().required(),
    intercom_unit_name_display: Yup.string().required()
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      intercom_name_format: INTERCOM_NAME_SETTINGS.FULL_NAME.value,
      intercom_unit_name_display: INTERCOM_UNIT_NAME_DISPLAY_SETTINGS.NONE.value
    }
  });

  useEffect(() => {
    if (!building?.id) return;
    const config = building.config || {};
    form.reset({
      intercom_name_format:
        config.intercom_name_format || INTERCOM_NAME_SETTINGS.FULL_NAME.value,
      intercom_unit_name_display:
        config.intercom_unit_name_display || INTERCOM_UNIT_NAME_DISPLAY_SETTINGS.NONE.value
    });
  }, [building?.id, building?.config, form]);

  const closeModal = () => {
    dispatch(clearBuilding());
    dispatch(changeOpenedModal({ modal: '' }));
  };

  const handleSubmit = (values) => {
    if (!building?.id) return;
    if (submitInProgressRef.current) return;
    submitInProgressRef.current = true;
    setIsUpdateInProgress(true);

    const updatedConfig = {
      ...(building.config || {}),
      intercom_name_format: values.intercom_name_format,
      intercom_unit_name_display: values.intercom_unit_name_display
    };

    const body = {
      name: building.name ?? '',
      address: building.address ?? '',
      city: building.city ?? '',
      state: building.state ?? '',
      zip: building.zip ?? '',
      country: building.country ?? '',
      photo: building.photo ?? '',
      contacts: (building.contacts || []).map((c) => ({
        ...(c.id && { id: c.id }),
        contact_name: c.contact_name || '',
        contact_email: c.contact_email || '',
        phone: c.phone || ''
      })),
      config: updatedConfig
    };

    dispatch(
      editBuilding({
        id: building.id,
        body,
        success: () => {
          // Update selected building in Redux immediately so UI reflects new intercom settings (same as Edit Building modal)
          dispatch(
            setGlobalSelectedBuilding({
              ...globalSelectedBuilding,
              id: building.id,
              config: { ...(globalSelectedBuilding?.config || {}), ...updatedConfig }
            })
          );
          const orgId = building.org_id ?? building.orgId;
          if (orgId) {
            dispatch(
              getBuildings({
                id: orgId,
                page: pagination?.currentPage ?? 1
              })
            );
            dispatch(getAllBuildings({ id: orgId }));
          }
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Intercom settings updated successfully.')
            })
          );
          closeModal();
          // Push updated settings to residents (sync to intercom)
          dispatch(
            pushResidentsToIntercom({
              orgId,
              buildingId: building.id,
              body: { sync_action: 'push_residents' },
              fail: () => {
                dispatch(
                  setToast({
                    toastShow: true,
                    toastMessage: t('Failed to push residents to intercom'),
                    modal: 'errorToast'
                  })
                );
              }
            })
          );
        },
        fail: () => {
          submitInProgressRef.current = false;
          setIsUpdateInProgress(false);
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Failed to update intercom settings'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const isFormReady = Boolean(building?.id);
  const isAkuvoxEnabled = !!building?.config?.enable_akuvox;
  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length > 0;

  return (
    <StyledModalV2 show={true} side width="550px">
      {!isValidBuildingId ? (
        <>
          <StyledModalBody>
            <ModalHeader title={t('Intercom Settings')} v2 />
            <ModalBodyContent>
              <p>{t('Please select a building to edit intercom settings.')}</p>
            </ModalBodyContent>
          </StyledModalBody>
          <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={true}>
            <ButtonBodyVariant type="button" variant="secondary" onClick={closeModal}>
              {t('Close')}
            </ButtonBodyVariant>
          </ModalButtonsContainer>
        </>
      ) : (
        <FormProvider {...form}>
          <Form {...form}>
            <form
                onSubmit={form.handleSubmit(handleSubmit)}
                style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
              >
              <StyledModalBody
                innerRef={(el) => (modalBodyRef.current = el)}
                cssOverride="flex: 1; min-height: 0;"
              >
                <ModalHeader title={t('Intercom Settings')} v2 />
                <ModalBodyContent>
                  <IntercomSettingsFormInputs isAkuvoxEnabled={!!building?.config?.enable_akuvox} />
                </ModalBodyContent>
              </StyledModalBody>
              <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
                <ButtonBodyVariant type="button" variant="secondary" onClick={closeModal}>
                  {t('Cancel')}
                </ButtonBodyVariant>
                <ButtonBodyVariant
                  type="submit"
                  disabled={isUpdateInProgress || (isFormReady && !hasDirtyFields) || !isAkuvoxEnabled}
                >
                  {t('Update')}
                </ButtonBodyVariant>
              </ModalButtonsContainer>
            </form>
          </Form>
        </FormProvider>
      )}
    </StyledModalV2>
  );
};

export default EditIntercomSettingsModal;
